import React, { useState, useEffect } from 'react';
import { useToast } from '../../../components/Toast';
import './StudentLifeCharacterShapingForm.css';

const ICON_OPTIONS = [
  'user-tie', 'users', 'book', 'home', 'handshake',
  'heart', 'brain', 'star', 'lightbulb', 'graduation-cap',
  'trophy', 'shield', 'smile', 'hands-helping', 'comments', 'user-friends'
];

const ZONE_CONFIG = {
  easternCulture: {
    label: 'Eastern Culture',
    color: 'orange',
    icon: 'compass',
    description: 'Cultural heritage and traditions'
  },
  sel: {
    label: 'Social Emotional Learning (SEL)',
    color: 'purple',
    icon: 'heart',
    description: 'Emotional and social development'
  },
  wellbeing: {
    label: 'Wellbeing',
    color: 'green',
    icon: 'leaf',
    description: 'Physical and mental wellness'
  }
};

export default function StudentLifeCharacterShapingForm({ apiBase }) {
  const toastUtil = useToast();
  const ToastContainer = toastUtil.ToastContainer;
  const toast = toastUtil;

  const [formData, setFormData] = useState({
    kicker: '',
    title: '',
    description: '',
    zones: {
      easternCulture: {
        title: '',
        subtitle: '',
        icon: '',
        pillars: []
      },
      sel: {
        title: '',
        subtitle: '',
        icon: '',
        pillars: []
      },
      wellbeing: {
        title: '',
        subtitle: '',
        icon: '',
        pillars: []
      }
    }
  });

  const [pendingFields, setPendingFields] = useState({
    kicker: false,
    title: false,
    description: false,
    zone_easternCulture: false,
    zone_sel: false,
    zone_wellbeing: false
  });

  const [originalData, setOriginalData] = useState({...formData});
  const [loading, setLoading] = useState(true);
  const [sectionId, setSectionId] = useState(null);
  const [submitting, setSubmitting] = useState({});
  const [modalOpenZone, setModalOpenZone] = useState(null);
  const [expandedPillar, setExpandedPillar] = useState(null);

  useEffect(() => {
    loadSection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiBase]);

  const loadSection = async () => {
    setLoading(true);
    try {
      console.log('[CharacterShapingForm] Loading section...');
      
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000); // 15 second timeout
      
      const response = await fetch(
        `${apiBase}/studentlife/cms/sections?_t=${Date.now()}`,
        {
          credentials: 'include',
          headers: { 'Cache-Control': 'no-cache' },
          signal: controller.signal
        }
      );

      clearTimeout(timeout);

      if (!response.ok) {
        toast.error('Failed to load section');
        setLoading(false);
        return;
      }

      const result = await response.json();
      
      if (result.ok && Array.isArray(result.data)) {
        const section = result.data.find(s => s.section_type === 'student-life-character-shaping');
        
        if (section) {
          console.log('[CharacterShapingForm] ✓ Section found:', section.id);
          setSectionId(section.id);
          
          const content = section.content_published || section.content;
          
          if (content) {
            const parsed = typeof content === 'string' ? JSON.parse(content) : content;
            
            const loadedData = {
              kicker: parsed.kicker || '',
              title: parsed.title || '',
              description: parsed.description || '',
              zones: {
                easternCulture: parsed.zones?.easternCulture || { title: '', subtitle: '', icon: '', pillars: [] },
                sel: parsed.zones?.sel || { title: '', subtitle: '', icon: '', pillars: [] },
                wellbeing: parsed.zones?.wellbeing || { title: '', subtitle: '', icon: '', pillars: [] }
              }
            };
            
            setFormData(loadedData);
            setOriginalData(JSON.parse(JSON.stringify(loadedData)));
            
            // Load pending status from pending_changes
            if (section.pending_changes) {
              const pending = typeof section.pending_changes === 'string' 
                ? JSON.parse(section.pending_changes) 
                : section.pending_changes;
              
              const newPending = {
                kicker: !!pending.kicker,
                title: !!pending.title,
                description: !!pending.description,
                zone_easternCulture: !!pending.zone_easternCulture,
                zone_sel: !!pending.zone_sel,
                zone_wellbeing: !!pending.zone_wellbeing
              };
              
              setPendingFields(newPending);
            }
            
            console.log('[CharacterShapingForm] ✓ Data loaded');
          }
        } else {
          console.log('[CharacterShapingForm] Section not found');
        }
      }
    } catch (error) {
      console.error('[CharacterShapingForm] Load error:', error);
      if (error.name === 'AbortError') {
        toast.error('Request timeout - server may be slow');
      } else {
        toast.error(`Error loading data: ${error.message}`);
      }
      setLoading(false);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleZoneChange = (zoneName, field, value) => {
    setFormData(prev => ({
      ...prev,
      zones: {
        ...prev.zones,
        [zoneName]: {
          ...prev.zones[zoneName],
          [field]: value
        }
      }
    }));
  };

  const handlePillarChange = (zoneName, pillarIdx, field, value) => {
    setFormData(prev => {
      const zone = { ...prev.zones[zoneName] };
      const pillars = [...zone.pillars];
      pillars[pillarIdx] = { ...pillars[pillarIdx], [field]: value };
      return {
        ...prev,
        zones: { ...prev.zones, [zoneName]: { ...zone, pillars } }
      };
    });
  };

  const handlePointChange = (zoneName, pillarIdx, pointIdx, value) => {
    setFormData(prev => {
      const zone = { ...prev.zones[zoneName] };
      const pillars = [...zone.pillars];
      const points = [...(pillars[pillarIdx].points || [])];
      points[pointIdx] = value;
      pillars[pillarIdx] = { ...pillars[pillarIdx], points };
      return {
        ...prev,
        zones: { ...prev.zones, [zoneName]: { ...zone, pillars } }
      };
    });
  };

  const addPillar = (zoneName) => {
    setFormData(prev => ({
      ...prev,
      zones: {
        ...prev.zones,
        [zoneName]: {
          ...prev.zones[zoneName],
          pillars: [...prev.zones[zoneName].pillars, { heading: '', points: [''] }]
        }
      }
    }));
  };

  const removePillar = (zoneName, pillarIdx) => {
    setFormData(prev => ({
      ...prev,
      zones: {
        ...prev.zones,
        [zoneName]: {
          ...prev.zones[zoneName],
          pillars: prev.zones[zoneName].pillars.filter((_, i) => i !== pillarIdx)
        }
      }
    }));
  };

  const addPoint = (zoneName, pillarIdx) => {
    setFormData(prev => {
      const zone = { ...prev.zones[zoneName] };
      const pillars = [...zone.pillars];
      pillars[pillarIdx] = {
        ...pillars[pillarIdx],
        points: [...(pillars[pillarIdx].points || []), '']
      };
      return {
        ...prev,
        zones: { ...prev.zones, [zoneName]: { ...zone, pillars } }
      };
    });
  };

  const removePoint = (zoneName, pillarIdx, pointIdx) => {
    setFormData(prev => {
      const zone = { ...prev.zones[zoneName] };
      const pillars = [...zone.pillars];
      pillars[pillarIdx] = {
        ...pillars[pillarIdx],
        points: pillars[pillarIdx].points.filter((_, i) => i !== pointIdx)
      };
      return {
        ...prev,
        zones: { ...prev.zones, [zoneName]: { ...zone, pillars } }
      };
    });
  };

  const handleSubmitField = async (fieldName) => {
    if (!sectionId) {
      toast.error('Section not loaded');
      return;
    }

    setSubmitting(prev => ({ ...prev, [fieldName]: true }));

    try {
      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          section_id: sectionId,
          field_id: fieldName,
          field_name: fieldName,
          old_value: originalData[fieldName],
          new_value: formData[fieldName],
          locale: 'en',
          operation_type: 'update'
        })
      });

      const result = await response.json();
      
      if (result.success) {
        toast.success(`${fieldName} submitted for approval`);
        setPendingFields(prev => ({ ...prev, [fieldName]: true }));
      } else {
        if (response.status === 409) {
          toast.error(`${fieldName} already has pending approval`);
        } else {
          toast.error(result.error || 'Submit failed');
        }
      }
    } catch (err) {
      console.error('[CharacterShapingForm] Submit error:', err);
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleSubmitZone = async (zoneName) => {
    if (!sectionId) {
      toast.error('Section not loaded');
      return;
    }

    const fieldKey = `zone_${zoneName}`;
    setSubmitting(prev => ({ ...prev, [fieldKey]: true }));

    try {
      const response = await fetch(`${apiBase}/cms/field-change/submit`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          section_id: sectionId,
          field_id: fieldKey,
          field_name: `zones.${zoneName}`,
          old_value: originalData.zones[zoneName],
          new_value: formData.zones[zoneName],
          locale: 'en',
          operation_type: 'update'
        })
      });

      const result = await response.json();
      
      if (result.success) {
        toast.success(`${zoneName} zone submitted for approval`);
        setPendingFields(prev => ({ ...prev, [fieldKey]: true }));
      } else {
        if (response.status === 409) {
          toast.error(`${zoneName} zone already has pending approval`);
        } else {
          toast.error(result.error || 'Submit failed');
        }
      }
    } catch (err) {
      console.error('[CharacterShapingForm] Submit error:', err);
      toast.error('Error: ' + err.message);
    } finally {
      setSubmitting(prev => ({ ...prev, [fieldKey]: false }));
    }
  };

  const renderZoneSection = (zoneName, zoneLabel) => {
    const zone = formData.zones[zoneName];
    const fieldKey = `zone_${zoneName}`;
    const isPending = pendingFields[fieldKey];
    const isSubmitting = submitting[fieldKey];
    
    const hasChanged = JSON.stringify(zone) !== JSON.stringify(originalData.zones[zoneName]);

    return (
      <div key={zoneName} className={`zone-card zone-${zoneName}`} onClick={() => setModalOpenZone(zoneName)}>
        <div className="zone-card-header">
          <div className="zone-icon">
            <i className={`fas fa-${ZONE_CONFIG[zoneName].icon}`}></i>
          </div>
          <div className="zone-card-content">
            <h3 className="zone-card-title">{zoneLabel}</h3>
            <p className="zone-card-subtitle">{zone.subtitle || ZONE_CONFIG[zoneName].description}</p>
          </div>
        </div>
        <div className="zone-card-footer">
          <div className="zone-stats">
            <span className="stat-item">{zone.pillars.length} Pillars</span>
            {isPending && <span className="status-badge pending">⏳ Pending</span>}
          </div>
          <div className="zone-chevron">
            <i className="fas fa-chevron-right"></i>
          </div>
        </div>
      </div>
    );
  };

  const renderZoneModal = () => {
    if (!modalOpenZone) return null;

    const zoneName = modalOpenZone;
    const zoneLabel = ZONE_CONFIG[zoneName].label;
    const zone = formData.zones[zoneName];
    const fieldKey = `zone_${zoneName}`;
    const isPending = pendingFields[fieldKey];
    const isSubmitting = submitting[fieldKey];
    const hasChanged = JSON.stringify(zone) !== JSON.stringify(originalData.zones[zoneName]);

    return (
      <div className="modal-overlay" onClick={() => setModalOpenZone(null)}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2 className="modal-title">{zoneLabel}</h2>
            <button
              className="modal-close-btn"
              onClick={() => setModalOpenZone(null)}
              title="Close modal"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          <div className="modal-body">
            {/* Zone Details Section */}
            <div className="modal-section">
              <h3 className="modal-section-title">Zone Details</h3>
              
              <div className="form-field">
                <label>Title</label>
                <input
                  type="text"
                  className="text-input"
                  value={zone.title}
                  onChange={(e) => handleZoneChange(zoneName, 'title', e.target.value)}
                  disabled={isPending || isSubmitting}
                  placeholder={`${zoneLabel} title`}
                />
              </div>

              <div className="form-field">
                <label>Subtitle</label>
                <input
                  type="text"
                  className="text-input"
                  value={zone.subtitle}
                  onChange={(e) => handleZoneChange(zoneName, 'subtitle', e.target.value)}
                  disabled={isPending || isSubmitting}
                  placeholder={`${zoneLabel} subtitle`}
                />
              </div>

              <div className="form-field">
                <label>Icon</label>
                <select
                  className="select-input"
                  value={zone.icon}
                  onChange={(e) => handleZoneChange(zoneName, 'icon', e.target.value)}
                  disabled={isPending || isSubmitting}
                >
                  <option value="">Select an icon</option>
                  {ICON_OPTIONS.map(icon => (
                    <option key={icon} value={icon}>{icon}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Pillars Section */}
            <div className="modal-section">
              <div className="section-header">
                <h3 className="modal-section-title">Pillars ({zone.pillars.length})</h3>
                <button
                  type="button"
                  className="add-pillar-btn"
                  onClick={() => addPillar(zoneName)}
                  disabled={isPending || isSubmitting}
                >
                  <i className="fas fa-plus"></i> Add Pillar
                </button>
              </div>

              <div className="pillars-list">
                {zone.pillars.length === 0 ? (
                  <p className="empty-state">No pillars yet. Click "Add Pillar" to get started.</p>
                ) : (
                  zone.pillars.map((pillar, pillarIdx) => (
                    <div key={pillarIdx} className="pillar-item">
                      <div 
                        className={`pillar-header ${expandedPillar === pillarIdx ? 'expanded' : ''}`}
                        onClick={() => setExpandedPillar(expandedPillar === pillarIdx ? null : pillarIdx)}
                      >
                        <div className="pillar-info">
                          <h4 className="pillar-title">{pillar.heading || `Pillar ${pillarIdx + 1}`}</h4>
                          <span className="pillar-count">{(pillar.points || []).length} points</span>
                        </div>
                        <div className="pillar-actions">
                          <button
                            type="button"
                            className="delete-icon-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              removePillar(zoneName, pillarIdx);
                            }}
                            disabled={isPending || isSubmitting}
                            title="Remove pillar"
                          >
                            <i className="fas fa-trash"></i>
                          </button>
                          <div className="pillar-chevron">
                            <i className="fas fa-chevron-down"></i>
                          </div>
                        </div>
                      </div>

                      {expandedPillar === pillarIdx && (
                        <div className="pillar-content">
                          <div className="form-field">
                            <label>Heading</label>
                            <input
                              type="text"
                              className="text-input"
                              value={pillar.heading || ''}
                              onChange={(e) => handlePillarChange(zoneName, pillarIdx, 'heading', e.target.value)}
                              disabled={isPending || isSubmitting}
                              placeholder="Pillar heading"
                            />
                          </div>

                          <div className="form-field">
                            <div className="section-header">
                              <label>Points ({(pillar.points || []).length})</label>
                              <button
                                type="button"
                                className="add-point-btn"
                                onClick={() => addPoint(zoneName, pillarIdx)}
                                disabled={isPending || isSubmitting}
                              >
                                <i className="fas fa-plus"></i> Add
                              </button>
                            </div>

                            <div className="points-list">
                              {(pillar.points || []).map((point, pointIdx) => (
                                <div key={pointIdx} className="point-item">
                                  <input
                                    type="text"
                                    className="text-input"
                                    value={point}
                                    onChange={(e) => handlePointChange(zoneName, pillarIdx, pointIdx, e.target.value)}
                                    disabled={isPending || isSubmitting}
                                    placeholder={`Point ${pointIdx + 1}`}
                                  />
                                  {pillar.points.length > 1 && (
                                    <button
                                      type="button"
                                      className="delete-icon-btn"
                                      onClick={() => removePoint(zoneName, pillarIdx, pointIdx)}
                                      disabled={isPending || isSubmitting}
                                    >
                                      <i className="fas fa-times"></i>
                                    </button>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="modal-cancel-btn"
              onClick={() => setModalOpenZone(null)}
            >
              Close
            </button>
            <button
              onClick={() => handleSubmitZone(zoneName)}
              disabled={isSubmitting || isPending || !hasChanged}
              className="submit-btn"
            >
              {isSubmitting ? 'Submitting...' : 'Submit for Approval'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="education-hero-form-container">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading Character Shaping Section...</p>
          <p style={{fontSize: '12px', color: '#a0aec0', marginTop: '8px'}}>
            This may take a moment if the server is slow
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="education-hero-form-container">
      <ToastContainer />

      <div className="hero-form-wrapper">
        <div className="hero-form-header">
          <h2 className="hero-form-title">Character Shaping</h2>
          <p className="hero-form-description">
            Manage Character Shaping section with three zones
          </p>
        </div>

        {/* KICKER SECTION */}
        <div className="form-section">
          <h3 className="form-section-title">Section Kicker</h3>
          <div className="form-field-card">
            <div className="field-header">
              <label>Kicker</label>
              {pendingFields.kicker && (
                <span className="status-badge pending">⏳ Pending</span>
              )}
            </div>
            <input
              type="text"
              value={formData.kicker}
              onChange={(e) => handleFieldChange('kicker', e.target.value)}
              disabled={submitting.kicker || pendingFields.kicker}
              placeholder="e.g., Character Shaping"
              className="text-input"
            />
            <button
              onClick={() => handleSubmitField('kicker')}
              disabled={submitting.kicker || pendingFields.kicker || formData.kicker === originalData.kicker}
              className="submit-btn"
            >
              {submitting.kicker ? 'Submitting...' : 'Submit for Approval'}
            </button>
          </div>
        </div>

        {/* TITLE SECTION */}
        <div className="form-section">
          <h3 className="form-section-title">Section Title</h3>
          <div className="form-field-card">
            <div className="field-header">
              <label>Title *</label>
              {pendingFields.title && (
                <span className="status-badge pending">⏳ Pending</span>
              )}
            </div>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => handleFieldChange('title', e.target.value)}
              disabled={submitting.title || pendingFields.title}
              placeholder="e.g., Character Shaping at Permata Harapan"
              className="text-input"
            />
            <button
              onClick={() => handleSubmitField('title')}
              disabled={submitting.title || pendingFields.title || formData.title === originalData.title}
              className="submit-btn"
            >
              {submitting.title ? 'Submitting...' : 'Submit for Approval'}
            </button>
          </div>
        </div>

        {/* DESCRIPTION SECTION */}
        <div className="form-section">
          <h3 className="form-section-title">Section Description</h3>
          <div className="form-field-card">
            <div className="field-header">
              <label>Description</label>
              {pendingFields.description && (
                <span className="status-badge pending">⏳ Pending</span>
              )}
            </div>
            <textarea
              value={formData.description}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              disabled={submitting.description || pendingFields.description}
              placeholder="Brief description of character shaping program..."
              className="textarea-input"
              rows="4"
            />
            <button
              onClick={() => handleSubmitField('description')}
              disabled={submitting.description || pendingFields.description || formData.description === originalData.description}
              className="submit-btn"
            >
              {submitting.description ? 'Submitting...' : 'Submit for Approval'}
            </button>
          </div>
        </div>

        {/* ZONES GRID - Card-based */}
        <div className="zones-grid">
          {renderZoneSection('easternCulture', ZONE_CONFIG.easternCulture.label)}
          {renderZoneSection('sel', ZONE_CONFIG.sel.label)}
          {renderZoneSection('wellbeing', ZONE_CONFIG.wellbeing.label)}
        </div>

        {/* ZONE MODAL */}
        {renderZoneModal()}
      </div>
    </div>
  );
}
